"use client"
import { type MutableRefObject, useCallback, useEffect, useRef } from "react"
import type { ResolvedBranchesData } from "@/lib/branchTypes"
import {
  type SetSelectedPathOptions,
  buildPathToNode,
  getCenteredScrollTop,
  stabilizeScrollToElementId,
} from "../data/playbookStateUtils"
import { type AnchorTarget, resolveAnchor } from "../data/anchorIndex"
import { EPILOGUE_HEADING_ID, POV_HEADING_IDS } from "../branch-node/plan-a-timeline-diagram/planATimelineDiagramModel"
import { type PerspectiveCategory } from "@/app/common/blocks/perspectiveTabContext"

// Reverse of POV_HEADING_IDS: hash id -> perspective, to mount the POV
// body a #playbook-*-pov deep link targets before scrolling to it.
const POV_PERSPECTIVE_BY_HEADING_ID: Record<string, Exclude<PerspectiveCategory, 'mainScenario'>> =
  Object.fromEntries(
    (Object.entries(POV_HEADING_IDS) as [Exclude<PerspectiveCategory, 'mainScenario'>, string][])
      .map(([perspective, id]) => [id, perspective]),
  )

// Runtime ending-open: glide the ending's own title to the middle of the
// viewport with ONE smooth scroll. The title's document position is stable
// from the first frame (the clipped body lays out at full height and nothing
// above it moves), but the scroll ROOM below is not: at the hub the reader
// can sit at max scroll, and a smooth scroll fired before the 300ms height
// animation has grown the document clamps at the then-current max and lands
// short. So each frame compute the target CLAMPED to the current max scroll
// and wait until that stops moving (expansion finished growing the page)
// before firing. Polling for the element first covers the epilogue body,
// which mounts on the same click.
const ENDING_CENTER_FIND_MAX_FRAMES = 40 // ~0.6s at 60fps; missing target fails quietly
const ENDING_CENTER_STABLE_FRAMES = 6
const ENDING_CENTER_MAX_FRAMES = 120 // ~2s at 60fps
const smoothScrollEndingTitleToCenter = (
  headingId: string,
  requestIdRef: { current: number },
) => {
  const requestId = ++requestIdRef.current
  let lastTarget = Number.NaN
  let stableFrames = 0
  let frames = 0
  const tick = () => {
    if (requestIdRef.current !== requestId) return
    const el = document.getElementById(headingId)
    if (!el) {
      if (++frames < ENDING_CENTER_FIND_MAX_FRAMES) requestAnimationFrame(tick)
      return
    }
    const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    const centeredTop = el.getBoundingClientRect().top + window.scrollY - window.innerHeight / 2
    const target = Math.min(Math.max(0, centeredTop), maxScrollTop)
    stableFrames = Math.abs(target - lastTarget) < 1 ? stableFrames + 1 : 0
    lastTarget = target
    if (stableFrames >= ENDING_CENTER_STABLE_FRAMES || ++frames >= ENDING_CENTER_MAX_FRAMES) {
      if (Math.abs(window.scrollY - target) > 1) {
        window.scrollTo({ top: target, behavior: "smooth" })
      }
      return
    }
    requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

type UseAnchorDeepLinkArgs = {
  branchData: ResolvedBranchesData
  anchorIndex: Map<string, AnchorTarget>
  parentMap: Record<string, string>
  setSelectedPath: (path: string[], options?: SetSelectedPathOptions) => void
  // True once user manually picked a branch; blocks the late
  // initial-load effect from yanking them back during hydration.
  hasUserNavigatedRef: MutableRefObject<boolean>
  // Mount the collapsed Plan A epilogue before the scroll loop finds it.
  onExpandEpilogue: () => void
  // Mount one inline POV body; the three endings are mutually exclusive,
  // so this collapses the others.
  onExpandPov: (perspective: Exclude<PerspectiveCategory, 'mainScenario'>) => void
  // Collapse whichever ending is open. Fired when the hash is cleared —
  // the nav grid's toggle-off (closeSelectedEnding) empties the hash so the
  // open ending animates back to height 0.
  onCollapseEndings: () => void
}

// URL-hash deep linking: expand the branch path containing the anchored
// element and scroll to it, on initial load and on hashchange.
export function useAnchorDeepLink({
  branchData,
  anchorIndex,
  parentMap,
  setSelectedPath,
  hasUserNavigatedRef,
  onExpandEpilogue,
  onExpandPov,
  onCollapseEndings,
}: UseAnchorDeepLinkArgs) {
  // Cancels an in-flight scroll-retry loop when a newer hash arrives.
  const scrollRequestIdRef = useRef(0)

  const scrollToElement = useCallback(
    (elementId: string) => {
      // Re-aim each frame until resting position holds, so reflow from
      // hydrating content above can't leave us short (see
      // stabilizeScrollToElementId); force :::details open to measure it.
      stabilizeScrollToElementId(elementId, scrollRequestIdRef, {
        align: getCenteredScrollTop,
        beforeScroll: (el) => {
          if (el instanceof HTMLDetailsElement) el.open = true
        },
      })
    },
    [],
  )

  const applyHash = useCallback(
    (rawHash: string, isInitialLoad: boolean) => {
      const target = resolveAnchor(rawHash, anchorIndex, branchData)

      if (target) {
        const path = buildPathToNode(target.nodeId, parentMap)
        if (path.length === 0) return
        setSelectedPath(path, { scrollTargetNodeId: null })
        scrollToElement(target.elementId)
        return
      }

      // Fallback for hashes not in the anchor index but present in the
      // DOM (e.g. date markers like "narrative-2026-01-01").
      let key = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash
      try { key = decodeURIComponent(key) } catch { /* keep raw */ }
      // Epilogue/POV headings exist only once expanded; mount first so
      // the scroll loop can find them.
      if (key === EPILOGUE_HEADING_ID) onExpandEpilogue()
      const povPerspective = POV_PERSPECTIVE_BY_HEADING_ID[key]
      if (povPerspective) onExpandPov(povPerspective)
      if (key === EPILOGUE_HEADING_ID || povPerspective) {
        // A deep-linked load still lands ON the ending via the snap-scroll
        // machinery below; but a runtime click comes from the end-cap
        // controls, and the body expands in place as a pure height animation
        // — glide the ending's own title to the middle of the viewport once
        // the expansion has made room for it (see the helper above). This
        // also cancels any in-flight scroll loop. Closing (empty hash) never
        // touches scroll at all.
        if (!isInitialLoad) {
          smoothScrollEndingTitleToCenter(key, scrollRequestIdRef)
          return
        }
      }
      if (key) scrollToElement(key)
    },
    [anchorIndex, branchData, parentMap, setSelectedPath, scrollToElement, onExpandEpilogue, onExpandPov],
  )

  // Initial load: apply the hash once, after mount.
  const didInitRef = useRef(false)
  useEffect(() => {
    if (didInitRef.current) return
    didInitRef.current = true
    if (hasUserNavigatedRef.current) return
    const hash = window.location.hash
    if (hash && hash.length > 1) applyHash(hash, true)
  }, [applyHash, hasUserNavigatedRef])

  // Runtime hash changes (in-page anchor clicks, manual URL edits).
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash
      if (hash && hash.length > 1) applyHash(hash, false)
      // Hash cleared: the nav grid's toggle-off — close any open ending.
      else onCollapseEndings()
    }
    window.addEventListener("hashchange", handler)
    return () => window.removeEventListener("hashchange", handler)
  }, [applyHash, onCollapseEndings])
}
